import { UIUtilStrings } from "../../../../ui/util/Strings";

const removeDiacritics = UIUtilStrings._StringRemoveDiacriticMarks;

// Normaliza texto para búsqueda tolerante: minúsculas, sin diacríticos y espacios colapsados
const normalizeForSearch = (value: string): string => {
    return removeDiacritics((value || "").toLowerCase())
        .replace(/\s+/g, " ")
        .trim();
};

/**
 * Evalúa coincidencia por cadena compacta, por tokens individuales, o por iniciales.
 * @example
 * matchQueryWithValue("juan perez", "Juan Pérez") // true (coincidencia por tokens)
 * matchQueryWithValue("juanperez", "Juan Pérez") // true (coincidencia compacta)
 * matchQueryWithValue("ju pe", "Juan Pérez") // true (coincidencia por tokens)
 * matchQueryWithValue("jp", "Juan Pérez") // true (coincidencia por iniciales)
 * matchQueryWithValue("jcp", "Juan Carlos Pérez") // true (coincidencia por iniciales)
 */
const matchQueryWithValue = (query: string, value: string): boolean => {
    const normalizedQuery = normalizeForSearch(query);
    if (!normalizedQuery) {
        return true;
    }

    const queryTokens: string[] = normalizedQuery.split(" ").filter(Boolean); // Tokens individuales de la búsqueda
    const compactQuery: string = normalizedQuery.replace(/\s+/g, ""); // Versión compacta de la búsqueda (sin espacios)

    const normalizedValue = normalizeForSearch(value);
    const compactValue = normalizedValue.replace(/\s+/g, "");

    // >> Coincidencia Compacta (ej. "juanperez" contiene "juanpe")
    if (compactValue.includes(compactQuery)) {
        return true;
    }

    // >> Coincidencia por Iniciales / Acrónimos (ej. "jp" coincide con "Juan Pérez")
    const valueInitials: string = normalizedValue
        .split(/\s+/)
        .filter(Boolean)
        .map(word => word[0])
        .join("");

    // Verificar si la búsqueda compacta (ej. "jp") está dentro de las iniciales
    if (valueInitials.includes(compactQuery)) {
        return true;
    }

    // >> Coincidencia por Tokens (ej. "ju pe" -> "juan" contiene "ju", "perez" contiene "pe")
    return queryTokens.every((token) => normalizedValue.includes(token));
};

/**
 * Variante Simple:
 * Determina si una consulta de búsqueda coincide con AL MENOS UNO de los valores proporcionados.
 * @param query La cadena de texto a buscar.
 * @param searchValues Arreglo de cadenas donde se buscará la coincidencia.
 * @returns true si hay coincidencia en algún valor.
 */
export const matchesSearchQuery = (query: string, searchValues: string[]): boolean => {
    if (!searchValues || searchValues.length === 0) return false;

    return searchValues.some((value) => matchQueryWithValue(query, value));
};

/**
 * Variante Avanzada:
 * Determina si una consulta coincide con los valores o con un ID (incluyendo variaciones operativas comunes del ID).
 * @param query La cadena de texto a buscar.
 * @param idValue El identificador principal del registro.
 * @param searchValues Arreglo de cadenas adicionales (ej. nombres, descripciones).
 */
export const matchesSearchQueryWithIdVariants = (query: string, idValue: string, searchValues: string[]): boolean => {
    const normalizedId = normalizeForSearch(idValue);

    // Variaciones del ID
    const idVariations = [
        normalizedId,
        `0${normalizedId}`,
        `00${normalizedId}`,
        `${normalizedId}.0`,
        `0${normalizedId}.0`,
        `00${normalizedId}.0`,
    ];

    // Reutilizamos la variante simple, concatenando los arreglos
    return matchesSearchQuery(query, [...searchValues, ...idVariations]);
};